import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { addDays, format } from "date-fns";
import { Phone, MessageCircle, Footprints, Users, Mail, X, Search } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { useProto } from "./ProtoContext";
import { PEOPLE, MY_PEOPLE, ALL_PEOPLE } from "./data";
import { Avatar, fullName } from "./ui";
import { Sheet } from "./Sheet";
import { RequestPerson } from "./RequestPerson";
import { cn } from "@/lib/utils";

const METHODS = [
  { id: "phone", label: "Phone", Icon: Phone },
  { id: "text", label: "Text", Icon: MessageCircle },
  { id: "visit", label: "Visit", Icon: Footprints },
  { id: "in_person", label: "In person", Icon: Users },
  { id: "email", label: "Email", Icon: Mail },
];
const FOLLOW = [
  { id: "1", label: "Tomorrow", days: 1 },
  { id: "3", label: "3 days", days: 3 },
  { id: "7", label: "1 week", days: 7 },
  { id: "date", label: "Choose date" },
  { id: "none", label: "No follow-up" },
];


const Picker = ({ onPick }) => {
  const { role } = useProto();
  const [q, setQ] = useState("");
  const ids = (role === "pastor" ? ALL_PEOPLE : MY_PEOPLE).filter((id) => fullName(PEOPLE[id]).toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="log-care-picker">
      <h2 className="t-title">Who did you care for?</h2>
      <label className="field mt-5 flex items-center gap-3 h-14 px-4">
        <Search size={20} className="text-ink-faint" />
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your people" className="flex-1 bg-transparent outline-none text-[17px]" data-testid="log-care-picker-search" />
      </label>
      <div className="mt-3">
        {ids.slice(0, 6).map((id) => (
          <button key={id} onClick={() => onPick(id)} className="w-full flex items-center gap-3 py-3 px-2 rounded-[12px] hover:bg-surface text-left" data-testid={`log-care-pick-${id}`}>
            <Avatar person={PEOPLE[id]} size={44} /><span className="text-lg text-ink">{fullName(PEOPLE[id])}</span>
          </button>
        ))}
      </div>
      <RequestPerson initial={ids.length ? "" : q} key={ids.length ? "r" : q} />
    </div>
  );
};

const Success = ({ p, next, onDone }) => {
  const { me } = useProto();
  return (
    <div className="flex flex-col items-center text-center py-10" data-testid="log-care-success">
      <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", damping: 12, stiffness: 200 }} className="h-24 w-24 rounded-full bg-calm-soft flex items-center justify-center">
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
          <motion.path d="M12 25l8 8 16-17" stroke="var(--calm)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 0.2, duration: 0.45 }} />
        </svg>
      </motion.div>
      <h2 className="t-greeting text-[38px] mt-6">Thank you, {me.first}.</h2>
      <p className="text-lg text-ink-soft mt-3 max-w-sm">Your care for {p.first} is saved.</p>
      <p className="text-lg text-ink mt-1 max-w-sm" data-testid="log-care-success-next">{next ? <>Next check-in: <b>{next}</b>. It'll be on your Today list.</> : <>No follow-up scheduled. {p.first} stays in your care.</>}</p>
      <button onClick={onDone} className="btn btn-lg mt-10 px-10 bg-ink text-paper" data-testid="log-care-done-btn">Done</button>
    </div>
  );
};

const Form = ({ p, preset, onSaved }) => {
  const [method, setMethod] = useState(preset || null);
  const [note, setNote] = useState("");
  const [follow, setFollow] = useState(null);
  const [date, setDate] = useState(null);
  const nextDate = follow === "date" ? date : FOLLOW.find((f) => f.id === follow)?.days && addDays(new Date(), FOLLOW.find((f) => f.id === follow).days);
  const ready = method && follow && (follow !== "date" || date);
  const save = () => onSaved({ method, methodLabel: METHODS.find((m) => m.id === method).label, nextLabel: nextDate ? format(nextDate, "EEEE, MMM d") : null });
  return (
    <div data-testid="log-care-form">
      <div className="flex items-center gap-3"><Avatar person={p} size={48} /><div><p className="t-label">Log care for</p><p className="t-name-lg mt-0.5">{fullName(p)}</p></div></div>
      <h3 className="t-heading-sm mt-9">How did you connect?</h3>
      <div className="grid grid-cols-3 gap-2 mt-4">
        {METHODS.map(({ id, label, Icon }) => (
          <button key={id} onClick={() => setMethod(id)} aria-pressed={method === id} className={cn("choice", "h-[76px] flex flex-col items-center justify-center gap-1.5")} data-testid={`log-method-${id}`}>
            <Icon size={20} />{label}
          </button>
        ))}
      </div>
      <h3 className="t-heading-sm mt-9">Anything the care team should know?</h3>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder={`e.g. Feeling a bit better. June could use a meal this week.`} className="field mt-4 p-4 leading-relaxed resize-none placeholder:text-ink-faint" data-testid="log-care-note" />
      <p className="text-sm text-ink-faint mt-2">Shared with {p.first}'s care team. Private pastoral notes belong in Confidential.</p>
      <h3 className="t-heading-sm mt-9">Follow up again?</h3>
      <div className="flex flex-wrap gap-2 mt-4">
        {FOLLOW.map((f) => (
          <button key={f.id} onClick={() => setFollow(f.id)} aria-pressed={follow === f.id} className={cn("choice", "h-12 px-5")} data-testid={`log-follow-${f.id}`}>
            {f.id === "date" && date && follow === "date" ? format(date, "MMM d") : f.label}
          </button>
        ))}
      </div>
      {follow === "date" && (
        <div ref={(el) => el?.scrollIntoView({ behavior: "smooth", block: "center" })} className="mt-3 surface inline-block" data-testid="log-follow-calendar">
          <Calendar mode="single" selected={date} onSelect={setDate} disabled={{ before: addDays(new Date(), 1) }} />
        </div>
      )}
      <div className="sticky bottom-0 -mx-6 px-6 pt-6 pb-6 mt-4 bg-gradient-to-t from-paper via-paper to-transparent">
        <button disabled={!ready} onClick={save} className="btn btn-primary btn-lg w-full" data-testid="log-care-save-btn">Save</button>
      </div>
    </div>
  );
};

export default function LogCare() {
  const { sheet, closeLog, completeCare, isPhone } = useProto();
  const [personId, setPersonId] = useState(null);
  const [result, setResult] = useState(null);
  useEffect(() => {
    if (sheet.open) { setPersonId(sheet.personId); setResult(null); }
  }, [sheet]);
  const p = personId && PEOPLE[personId];
  const saved = (info) => { completeCare(personId, info); setResult(info); };
  return (
    <Sheet open={sheet.open} onClose={closeLog} isPhone={isPhone}>
      <div className="px-6 pt-4 pb-2">
        <div className="flex justify-end"><button onClick={closeLog} aria-label="Close" className="h-11 w-11 rounded-full bg-sunk flex items-center justify-center text-ink-soft" data-testid="log-care-close-btn"><X size={18} /></button></div>
        {!p && <Picker onPick={setPersonId} />}
        {p && !result && <Form key={personId} p={p} preset={sheet.method} onSaved={saved} />}
        {p && result && <Success p={p} next={result.nextLabel} onDone={closeLog} />}
      </div>
    </Sheet>
  );
}
